import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Grid,
    Alert,
    Skeleton,
    Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import CourseIcon from '../../../assets/images/icons/CourseIcon';
import { formatDateOnly } from "../../../utils/formatDateTime";
import CardComponent from '../../../components/cards/CourseCard';
import { APP_URL } from '../../../config';
import { useGetValidCoursesQuery } from '../../../store/services/university-service';
import { useGetTenancyAllowRetakeQuery } from "../../../store/services/tenancy.service";
import { useSelector } from 'react-redux';
import { useDynamicBreakpoints } from '../../../utils/breakpointUtils';
import GlobalPagination from '../../../components/GlobalPagination';
import MainCard from "../../../components/cards/MainCard";
import { getCourseMediaUrls } from '../../../utils/coursePathUtils';

const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.35 } },
    exit: { opacity: 0, transition: { duration: 0.35 } }
};

const cardVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 100,
            damping: 15,
            duration: 0.35
        }
    }
};

const DEFAULT_GIF = '/default.gif';
const DEFAULT_IMG = '/default.png';

const YourCourses = ({
    isDataLoaded,
    userOutstandingCourses,
    userOutstandingCoursesLoading,
    userOutstandingCoursesError,
    showCourseContent
}) => {
    const theme = useTheme();
    const { tenancyId } = useSelector((state) => state.tenancy);
    const { drawerOpen } = useSelector((state) => state.menu);
    const { matchDownSM, matchDownMD, matchDownLG } = useDynamicBreakpoints(drawerOpen);
    const [currentIndex, setCurrentIndex] = useState(0);
    const carouselRef = useRef(null);

    const { data: tenancyRetakeData } = useGetTenancyAllowRetakeQuery({ tenancyId: tenancyId });
    const { data: coursesData } = useGetValidCoursesQuery({
        perPage: 10000,
        page: 1,
        includes: '',
        query: '',
        additionalQueryParams: `&published=true&tenancyId=${tenancyId}`
    });

    // Function to check if a course is recent (within 30 days)
    const isRecent = (dateString, days = 30) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= days;
    };

    // Sort outstanding courses by date (newest first)
    const sortedOutstandingCourses = userOutstandingCourses
        ? [...userOutstandingCourses].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated))
        : [];

    // Number of courses to display at once
    const coursesPerView = matchDownSM ? 1 : (matchDownLG ? 2 : 3);

    // Calculate the total number of "pages" in the carousel
    const totalPages = sortedOutstandingCourses ? Math.ceil(sortedOutstandingCourses.length / coursesPerView) : 0;

    // Get visible courses for the current page
    const getVisibleCourses = () => {
        if (!sortedOutstandingCourses) return [];
        const startIndex = currentIndex;
        return sortedOutstandingCourses.slice(startIndex, startIndex + coursesPerView);
    };

    // Calculate current "page" for the dots
    const currentPage = Math.floor(currentIndex / coursesPerView) + 1;

    // Handle pagination change
    const handlePageChange = (event, page) => {
        const newIndex = (page - 1) * coursesPerView;
        setCurrentIndex(newIndex);
    };

    // Error state
    if (userOutstandingCoursesError) {
        return (
            <Grid item xs={12}>
                <Alert id="course-load-error" severity="error">
                    An error occurred while fetching the courses.
                </Alert>
            </Grid>
        );
    }

    // No courses state
    if (userOutstandingCourses && userOutstandingCourses.length === 0 && !userOutstandingCoursesLoading) {
        return (
            <Grid item xs={12}>
                <MainCard
                    content={false}
                    sx={{
                        width: "100%",
                        bgcolor: "background.default",
                        alignSelf: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    border={false}
                    boxShadow={true}
                >
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                            <CourseIcon sx={{ mr: 0.5, fontSize: '1.5rem' }} color="primary" />
                            Your Courses
                        </Typography>
                    </Box>
                    <Box sx={{ mt: 3, mb: 4, px: 3 }}>
                        <Typography variant="h6" align="center" sx={{ mt: 4 }}>
                            No courses available at the moment.
                        </Typography>
                    </Box>
                </MainCard>
            </Grid>
        );
    }

    return (
        <Grid item xs={12}>
            {isDataLoaded && (
                <Box sx={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                    <Box sx={{
                        pb: 2,
                        pt: 2,
                        px: 3
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                            <CourseIcon sx={{ mr: 1, fontSize: '1.3rem' }} color="primary" />
                            Your Courses
                        </Typography>
                    </Box>

                    {/* Course Carousel Content */}
                    <Box sx={{
                        position: 'relative',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        px: { xs: 1, sm: 4 },
                        pb: { xs: 4, sm: 2 }
                    }}>
                        {/* Carousel Content */}
                        <Box
                            ref={carouselRef}
                            sx={{
                                width: '100%',
                                maxWidth: '1200px',
                                margin: '0 auto',
                                px: { xs: 2, sm: 6 },
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}
                        >
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentIndex}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    variants={containerVariants}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'center',
                                        gap: theme.spacing(matchDownSM ? 1 : 3)
                                    }}
                                >
                                    {userOutstandingCoursesLoading ? (
                                        [...Array(coursesPerView)].map((_, index) => (
                                            <Box key={index} sx={{
                                                width: `calc((100% - ${(coursesPerView - 1) * (matchDownSM ? 8 : 24)}px) / ${coursesPerView})`,
                                                flexShrink: 0
                                            }}>
                                                <Skeleton variant="rounded" height={0} width="100%" sx={{ paddingTop: '75%', marginBottom: 1 }} />
                                                <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="70%" />
                                                <Skeleton variant="rounded" height={50} width="100%" sx={{ marginTop: 1 }} />
                                            </Box>
                                        ))
                                    ) : (
                                        // Real course cards
                                        getVisibleCourses().map((course) => {
                                            const courseData = coursesData?.find((c) => c.id === course.universityCourseId);

                                            const { imageUrl, gifUrl } = getCourseMediaUrls(
                                                courseData?.courseContentPath,
                                                courseData?.isLms 
                                            );

                                            const course_img = imageUrl || DEFAULT_IMG;
                                            const course_gif = gifUrl || DEFAULT_GIF;

                                            const dateToShow = course.dateCreated;
                                            const formattedDate = formatDateOnly(new Date(dateToShow), 'dd-MM-yyyy');
                                            const isNewCourse = isRecent(course.dateCreated);

                                            return (
                                                <Box
                                                    key={course.id}
                                                    sx={{
                                                        width: `calc((100% - ${(coursesPerView - 1) * (matchDownSM ? 8 : 24)}px) / ${coursesPerView})`,
                                                        flexShrink: 0,
                                                        position: 'relative'
                                                    }}
                                                >
                                                    {/* NEW chip positioned absolutely */}
                                                    {isNewCourse && (
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 8,
                                                            zIndex: 10
                                                        }}>
                                                            <Chip
                                                                label="NEW"
                                                                size="small"
                                                                color="primary"
                                                                sx={{
                                                                    height: matchDownSM ? 20 : 25,
                                                                    fontSize: '0.70rem',
                                                                    fontWeight: 'bold',
                                                                    px: 0.5,
                                                                    '& .MuiChip-label': {
                                                                        px: 0.5
                                                                    }
                                                                }}
                                                            />
                                                        </Box>
                                                    )}

                                                    <motion.div
                                                        variants={cardVariants}
                                                        initial="hidden"
                                                        animate="visible"
                                                        whileHover={{ scale: 1.03, transition: { duration: 0.4 } }}
                                                        style={{ height: '100%' }}
                                                    >
                                                        <CardComponent
                                                            image={course_img}
                                                            title={course.universityCourseName}
                                                            gif={course_gif}
                                                            onClick={() => showCourseContent(course)}
                                                            type="outstanding"
                                                            dateLabel="Added:"
                                                            dateValue={formattedDate}
                                                            retake={tenancyRetakeData?.allowRetake && course.correctAnswers < 80 && course.correctAnswers !== null}
                                                        />
                                                    </motion.div>
                                                </Box>
                                            );
                                        })
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </Box>

                        <Box sx={{
                            pb: 1,
                            minHeight: '75px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            '& .MuiPagination-root': {
                                '& .MuiPaginationItem-root': {
                                    border: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                        }
                                    },
                                    '&.MuiPaginationItem-previousNext': {
                                        '&:hover': {
                                            backgroundColor: 'white',
                                            color: theme.palette.primary.main,
                                        }
                                    }
                                }
                            }
                        }}>
                            <GlobalPagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                showFirstButton={false}
                                showLastButton={false}
                            />
                        </Box>
                    </Box>
                </Box>
            )}
        </Grid>
    );
};

YourCourses.propTypes = {
    isDataLoaded: PropTypes.bool.isRequired,
    userOutstandingCourses: PropTypes.array,
    userOutstandingCoursesLoading: PropTypes.bool,
    userOutstandingCoursesError: PropTypes.object,
    showCourseContent: PropTypes.func.isRequired
};

export default YourCourses;